import React from 'react';
import { SCHEMATIC_DETAIL_RATIO } from '../../../utils/elevationPenWeights';
import {
  cabinetHardwarePlacement,
  frontDoorStyle,
} from '../../3d/geometry/proceduralRenderers/cabinetFrontLayout';
import { CabinetShell } from './CabinetFrame';
import {
  CabinetToeKick,
  DoorStyleInset,
  HardwareGlyph,
  HardwareProfile,
} from './CabinetParts';
import { Line, Rect, SchematicShell } from './SchematicPrimitives';

/**
 * The two orthographic views of a box cabinet, drawn straight from the
 * `cabinetFrontLayout` the 3D renderer builds its meshes from.
 *
 * Renderer coordinates are centred with +y up and +z toward the room; the
 * viewBox is top-left-origin with y down. FRONT maps x → x + width/2 and
 * y → height/2 − y. SIDE is the canonical LEFT view — the door face at
 * x = 0 and the wall at x = depth, so z → depth/2 − z — and the RIGHT view
 * mirrors it.
 */

const frontRect = (front, layout) => ({
  x: front.x - front.width / 2 + layout.width / 2,
  y: layout.height / 2 - (front.y + front.height / 2),
  width: front.width,
  height: front.height,
});

/**
 * The under-cabinet LED strip (`layout.lightStrip`) as ONE thin horizontal
 * line along its centre, in viewBox units: `x1` / `x2` are its two ends in
 * the view's horizontal axis (its length in the front view, its 12 mm depth
 * in a side view), `y` its centre height. It sits just below the carcass,
 * outside the shell — the shells draw with `overflow: visible`, exactly as
 * the 3D strip hangs below the box.
 */
function LightStripLine({ x1, x2, y, strokeColor, strokeWidth }) {
  return (
    <g data-cabinet-part="light-strip">
      <Line
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

// A HIDDEN line's dash, in drawing units (metres): the convention for what
// stands behind a surface — the pocket door's leaf in the wall draws so too
// — shortened to read on a 4" tray.
const HIDDEN_DASH = '0.02 0.012';
// …and drawn faint, so the doors in front of it still read first.
const HIDDEN_OPACITY = 0.55;

/**
 * The roll-out trays behind a base's doors (`cabinetRollOutSlots`), each as
 * a dashed outline of its front, in the front view only.
 */
function RollOutTrays({ slots, layout, strokeColor, strokeWidth }) {
  return slots.map((slot) => (
    <g key={slot.key} data-cabinet-part="rollout" data-name={slot.key}>
      <rect
        {...frontRect(slot.lip, layout)}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeOpacity={HIDDEN_OPACITY}
        strokeDasharray={HIDDEN_DASH}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  ));
}

const frontPoint = (x, y, layout) => ({
  x: x + layout.width / 2,
  y: layout.height / 2 - y,
});

/**
 * A rack's slats and dowels in the front view (`racks.rackPieces`): each bar
 * as its two long edges, turned with it. A plate rack's back row of dowels
 * stands behind its front row, so only the front row is drawn.
 */
function RackBars({ bars, layout, strokeColor, strokeWidth }) {
  return bars
    .filter((b) => !(b.z < 0))
    .map((b) => {
      const ux = (Math.cos(b.angle) * b.length) / 2;
      const uy = (Math.sin(b.angle) * b.length) / 2;
      const vx = (-Math.sin(b.angle) * b.thick) / 2;
      const vy = (Math.cos(b.angle) * b.thick) / 2;
      const edge = (s) => {
        const a = frontPoint(b.x - ux + s * vx, b.y - uy + s * vy, layout);
        const z = frontPoint(b.x + ux + s * vx, b.y + uy + s * vy, layout);
        return (
          <Line
            key={s}
            x1={a.x}
            y1={a.y}
            x2={z.x}
            y2={z.y}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      };
      return (
        <g key={b.name} data-cabinet-part="rack" data-name={b.name}>
          {edge(1)}
          {edge(-1)}
        </g>
      );
    });
}

/**
 * FRONT elevation: outer outline, the toe-kick line, and every front as its
 * own rectangle inside the reveal — so the 1/8" reveal and 5/16" gaps read
 * as a real full-overlay door set — with its style inset and hardware.
 */
export function CabinetFrontElevation({
  layout,
  doorStyle,
  hardwareType,
  // the trays behind a base's doors, drawn as hidden lines
  rollOutSlots = [],
  strokeColor,
  strokeWidth,
}) {
  const stroke = { strokeColor, strokeWidth };
  // Lines INSIDE a cabinet — the door-style inset, the pull, the toe kick —
  // are detail, and a drawing reads by that hierarchy. The carcass and each
  // front keep the object weight.
  const detail = {
    strokeColor,
    strokeWidth: strokeWidth * SCHEMATIC_DETAIL_RATIO,
  };
  // FLOATING shelves are their boards alone: no shell round them
  if (layout.openShelf?.floating)
    return (
      <svg
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%', overflow: 'visible' }}
      >
        {layout.openShelf.boards.map((board) => (
          <g key={board.name} data-cabinet-part="shelf" data-name={board.name}>
            <Rect {...frontRect(board, layout)} {...stroke} />
          </g>
        ))}
      </svg>
    );
  return (
    <CabinetShell width={layout.width} height={layout.height} {...stroke}>
      {/* An open shelf has no fronts: what shows is the shell's own edges and
          the shelves between them — the same boards the 3D box is built from. */}
      {layout.openShelf?.boards.map((board) => (
        <g key={board.name} data-cabinet-part="shelf" data-name={board.name}>
          <Rect {...frontRect(board, layout)} {...detail} />
        </g>
      ))}
      {layout.openShelf?.rack && (
        <RackBars
          bars={layout.openShelf.rack.bars}
          layout={layout}
          {...detail}
        />
      )}
      {layout.fronts.map((front) => {
        const rect = frontRect(front, layout);
        const placement = cabinetHardwarePlacement(front, hardwareType);
        return (
          <g
            key={front.name}
            data-cabinet-part={front.kind}
            data-name={front.name}
          >
            <Rect {...rect} {...stroke} />
            <DoorStyleInset
              {...rect}
              style={frontDoorStyle(layout, front, doorStyle)}
              isDrawer={front.isDrawer}
              {...detail}
            />
            {placement && (
              <HardwareGlyph
                placement={{
                  ...placement,
                  ...frontPoint(placement.x, placement.y, layout),
                }}
                {...detail}
              />
            )}
          </g>
        );
      })}
      <RollOutTrays slots={rollOutSlots} layout={layout} {...detail} />
      {layout.toeKick && (
        <CabinetToeKick
          width={layout.width}
          height={layout.height}
          {...detail}
        />
      )}
      {layout.lightStrip && (
        <LightStripLine
          x1={
            layout.lightStrip.x -
            layout.lightStrip.length / 2 +
            layout.width / 2
          }
          x2={
            layout.lightStrip.x +
            layout.lightStrip.length / 2 +
            layout.width / 2
          }
          y={layout.height / 2 - layout.lightStrip.y}
          {...detail}
        />
      )}
      {/* a glass holder's rails, hung under the box (`glassHolders.js`) */}
      {layout.glassHolder?.rails.map((rail) => (
        <g
          key={rail.name}
          data-cabinet-part="glass-holder"
          data-name={rail.name}
        >
          <Rect {...frontRect(rail, layout)} {...detail} />
        </g>
      ))}
    </CabinetShell>
  );
}

/**
 * BACK elevation: the carcass and its toe kick, and nothing else.
 *
 * What you see behind a run is a finished panel. Drawing the FRONT here —
 * which is what an unrecognised view used to fall back to — put doors, pulls
 * and drawer splits on the back of an island, so its two elevations came out
 * as mirror images of each other.
 */
export function CabinetBackElevation({ layout, strokeColor, strokeWidth }) {
  const stroke = { strokeColor, strokeWidth };
  return (
    <CabinetShell width={layout.width} height={layout.height} {...stroke}>
      {layout.toeKick && (
        <CabinetToeKick
          width={layout.width}
          height={layout.height}
          {...stroke}
        />
      )}
    </CabinetShell>
  );
}

/**
 * Vertical spans (viewBox y) the door slab occupies in side projection:
 * fronts in different columns overlap and merge; the gaps between stacked
 * fronts stay as breaks in the slab.
 */
function doorSlabSpans(layout) {
  const spans = layout.fronts
    .map((f) => {
      const top = layout.height / 2 - (f.y + f.height / 2);
      return { top, bottom: top + f.height };
    })
    .sort((a, b) => a.top - b.top);
  const merged = [];
  for (const span of spans) {
    const last = merged[merged.length - 1];
    if (last && span.top <= last.bottom + 1e-9) {
      last.bottom = Math.max(last.bottom, span.bottom);
    } else {
      merged.push({ ...span });
    }
  }
  return merged;
}

/**
 * SIDE elevation: the depth profile — carcass, the door slab standing
 * `doorThickness` proud of it, the toe-kick plinth recessed under the
 * carcass, and whatever hardware stands proud of the door face.
 */
export function CabinetSideElevation({
  layout,
  view,
  hardwareType,
  strokeColor,
  strokeWidth,
}) {
  const { depth, height, doorThickness } = layout;
  const stroke = { strokeColor, strokeWidth };
  const sideX = (z) => depth / 2 - z;
  const top = (centerY, h) => height / 2 - (centerY + h / 2);

  const carcass = {
    x: sideX(layout.boxCenterZ + layout.boxDepth / 2),
    y: top(layout.bodyCenterY, layout.bodyHeight),
    width: layout.boxDepth,
    height: layout.bodyHeight,
  };
  const kick = layout.toeKick && {
    x: sideX(layout.toeKick.centerZ + layout.toeKick.depth / 2),
    y: top(layout.toeKick.centerY, layout.toeKick.height),
    width: layout.toeKick.depth,
    height: layout.toeKick.height,
  };

  // FLOATING shelves side-on: each board, the full depth off the wall
  const profile = layout.openShelf?.floating ? (
    <g>
      {layout.openShelf.boards.map((b) => (
        <g key={b.name} data-cabinet-part="shelf" data-name={b.name}>
          <Rect
            x={0}
            y={top(b.y, b.height)}
            width={depth}
            height={b.height}
            {...stroke}
          />
        </g>
      ))}
    </g>
  ) : (
    <g>
      <g data-cabinet-part="carcass">
        <Rect {...carcass} {...stroke} />
      </g>
      {doorSlabSpans(layout).map((span) => (
        <g key={span.top} data-cabinet-part="door-slab">
          <Rect
            x={0}
            y={span.top}
            width={doorThickness}
            height={span.bottom - span.top}
            {...stroke}
          />
        </g>
      ))}
      {kick && (
        <g data-cabinet-part="toe-kick">
          <Rect {...kick} {...stroke} />
        </g>
      )}
      {layout.lightStrip && (
        <LightStripLine
          x1={sideX(layout.lightStrip.z + layout.lightStrip.depth / 2)}
          x2={sideX(layout.lightStrip.z - layout.lightStrip.depth / 2)}
          y={height / 2 - layout.lightStrip.y}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth * SCHEMATIC_DETAIL_RATIO}
        />
      )}
      {layout.fronts.map((front) => {
        const placement = cabinetHardwarePlacement(front, hardwareType);
        return (
          placement && (
            <g key={front.name} data-cabinet-part="hardware">
              <HardwareProfile
                placement={{ ...placement, y: height / 2 - placement.y }}
                {...stroke}
              />
            </g>
          )
        );
      })}
    </g>
  );

  return (
    <SchematicShell width={depth} height={height} {...stroke} drawFrame={false}>
      {view === 'right' ? (
        <g transform={`translate(${depth}, 0) scale(-1, 1)`}>{profile}</g>
      ) : (
        profile
      )}
    </SchematicShell>
  );
}
